import { GhGroup, InboxFilter, KnownApp, SavedScopeDoc } from "@kwp/contracts";
import { reviveTimestamps } from "@kwp/firestore";
import { z } from "zod";
import type { MailKindView } from "./kinds";
import { BOX_KEYS, type Box } from "./mailboxes";
import { sourceQuery, type SourceView } from "./sources";

/**
 * Which mail the inbox shows (architecture §1, §8): all accounts, some workspaces or some
 * accounts, one mailbox, and at most one quick filter. The URL carries it
 * (`?scope=all|ws:<id>|acct:<id>|saved:<id>`, `box`, `filter`, `tag`, `kind`, `app`, `gh`, `item`,
 * `repo` (a source, WP-062), `q` for a search,
 * `t` for the open thread and `a` for an open chat or task), so a view can be linked and the back
 * button works. No `scope` means the workspace in the path; no `box`, the inbox. The quick filters
 * have no chips since WP-056 (search does the same); saved scopes and links may still carry one.
 */
export const InboxSearch = z.object({
  scope: z.string().min(1).optional().catch(undefined),
  filter: InboxFilter.optional().catch(undefined),
  tag: z.string().min(1).max(100).optional().catch(undefined),
  q: z.string().min(1).max(2000).optional().catch(undefined),
  t: z.string().min(1).optional().catch(undefined),
  /** The mailbox (WP-056); none is the inbox. */
  box: z.enum(BOX_KEYS).optional().catch(undefined),
  /**
   * Mail, chats or tasks only (WP-054); or which kind of mail (WP-061): none is All mail with
   * chats and tasks (WP-065), `person` People with them, `notification|newsletter|receipt` that
   * kind's list; `all` (old links) is All mail.
   */
  kind: z.enum(["mail", "chat", "task", "person", "notification", "newsletter", "receipt", "all"]).optional().catch(undefined),
  /** A known app's mail (WP-062): GitHub, one of its groups, and inside a group PRs or issues or one repository. */
  app: KnownApp.optional().catch(undefined),
  gh: GhGroup.optional().catch(undefined),
  item: z.enum(["pr", "issue"]).optional().catch(undefined),
  repo: z
    .string()
    .regex(/^[a-z0-9_.-]+\/[a-z0-9_.-]+$/)
    .optional()
    .catch(undefined),
  /** The open chat or task row (`users/{me}/activity/{a}`), in place of a thread. */
  a: z.string().min(1).optional().catch(undefined),
});
export type InboxSearch = z.infer<typeof InboxSearch>;

export type Scope = { kind: "all" } | { kind: "workspaces"; ids: string[] } | { kind: "accounts"; ids: string[] };

export type SavedScope = SavedScopeDoc & { id: string };

export function toSavedScope(id: string, data: unknown): SavedScope | null {
  const parsed = SavedScopeDoc.safeParse(reviveTimestamps(data));
  return parsed.success ? { ...parsed.data, id } : null;
}

export function scopeOfSaved(saved: Pick<SavedScope, "scope">): Scope {
  if (saved.scope.accountIds.length > 0) return { kind: "accounts", ids: saved.scope.accountIds };
  if (saved.scope.workspaceIds.length > 0) return { kind: "workspaces", ids: saved.scope.workspaceIds };
  return { kind: "all" };
}

/** The scope a URL names; null when it names a saved scope that does not exist (any more). */
export function resolveScope(param: string | undefined, pathWorkspace: string, saved: readonly SavedScope[]): Scope | null {
  if (param === undefined) return { kind: "workspaces", ids: [pathWorkspace] };
  if (param === "all") return { kind: "all" };
  if (param.startsWith("ws:")) return { kind: "workspaces", ids: [param.slice(3)] };
  if (param.startsWith("acct:")) return { kind: "accounts", ids: [param.slice(5)] };
  if (param.startsWith("saved:")) {
    const found = saved.find((s) => s.id === param.slice(6));
    return found === undefined ? null : scopeOfSaved(found);
  }
  return { kind: "workspaces", ids: [pathWorkspace] };
}

/**
 * One Firestore constraint, described so the listener and the tests share it (and as JSON, the
 * listener's key). `asDate` marks a value given as epoch milliseconds that is queried as a Date.
 */
export type Constraint =
  | { field: string; op: "==" | "in" | "array-contains" | ">"; value: unknown; asDate?: true }
  | { orderBy: "lastMessageAt" | "snoozedUntil" | "sentAt"; direction: "asc" | "desc" };

/**
 * The accounts a scope covers; null for every account. A workspace is resolved through the
 * accounts' own `workspaceId` (the one place it is stored: threads do not carry it, §8), so
 * moving an account between workspaces moves its mail with no rewrite.
 */
export function scopeAccountIds(scope: Scope, accounts: ReadonlyArray<{ id: string; workspaceId: string }>): string[] | null {
  if (scope.kind === "all") return null;
  if (scope.kind === "accounts") return [...scope.ids];
  return accounts.filter((a) => scope.ids.includes(a.workspaceId)).map((a) => a.id);
}

/**
 * The unified inbox query (§8): collection group `threads`, `ownerUid == me` first (rules are not
 * filters, so the query must prove ownership), the scope's accounts (null: all), then the
 * mailbox: `location ==` the inbox, Archived, Spam or Trash, newest first (one index serves every
 * location), with at most one quick filter in the inbox; the inbox without a quick filter lists one
 * kind of mail (`kind ==`, WP-061; `all` none), since each quick filter with a kind would need
 * indexes of its own; a source (WP-062) lists a known app's inbox threads (`source.app ==`), one
 * GitHub group (`ghGroup ==`), and inside a group PRs or issues (`source.item ==`) or one repository
 * (`source.repo ==`), one at a time for the same reason; Snoozed is `snoozedUntil` set, soonest
 * first, and Sent `sentAt` set, newest send first (WP-055). Each shape has a composite index in
 * `firestore.indexes.json`. "none" when the scope holds no account: there is nothing to query.
 */
export function inboxConstraints(
  uid: string,
  accountIds: readonly string[] | null,
  box: Box,
  filter: InboxFilter | undefined,
  tag: string | undefined,
  kind: MailKindView = "all",
  source: SourceView | null = null,
): Constraint[] | "none" {
  if (accountIds !== null && accountIds.length === 0) return "none";
  const out: Constraint[] = [{ field: "ownerUid", op: "==", value: uid }];
  if (accountIds !== null) out.push(oneOrIn("accountId", accountIds));
  if (box === "snoozed") {
    out.push({ field: "snoozedUntil", op: ">", value: 0, asDate: true });
    out.push({ orderBy: "snoozedUntil", direction: "asc" });
    return out;
  }
  if (box === "sent") {
    out.push({ field: "sentAt", op: ">", value: 0, asDate: true });
    out.push({ orderBy: "sentAt", direction: "desc" });
    return out;
  }
  out.push({ field: "location", op: "==", value: box });
  if (box === "inbox") {
    if (filter === "unread") out.push({ field: "unread", op: "==", value: true });
    if (filter === "starred") out.push({ field: "starred", op: "==", value: true });
    if (filter === "attachment") out.push({ field: "hasAttachments", op: "==", value: true });
    if (filter === "tag" && tag !== undefined) out.push({ field: "tags", op: "array-contains", value: tag });
    if (filter === undefined && source !== null) out.push(...sourceConstraints(source));
    else if (filter === undefined && kind !== "all") out.push({ field: "kind", op: "==", value: kind });
  }
  out.push({ orderBy: "lastMessageAt", direction: "desc" });
  return out;
}

/**
 * The view as a search query, for "select all matching" (a plan over everything the view holds,
 * not just the loaded pages); null for views search cannot express (snoozed).
 */
export function viewQuery(box: Box, filter: InboxFilter | undefined, tag: string | undefined, kind: MailKindView = "all", source: SourceView | null = null): string | null {
  if (box === "snoozed") return null;
  if (box !== "inbox") return `in:${box}`;
  if (filter === undefined && source !== null) return `in:inbox ${sourceQuery(source)}`;
  if (filter === undefined && kind !== "all") return `in:inbox kind:${kind}`;
  switch (filter) {
    case "unread":
      return "in:inbox is:unread";
    case "starred":
      return "in:inbox is:starred";
    case "attachment":
      return "in:inbox has:attachment";
    case "tag":
      return tag === undefined ? "in:inbox" : `in:inbox tag:"${tag.replace(/"/g, "")}"`;
    default:
      return "in:inbox";
  }
}

function sourceConstraints(source: SourceView): Constraint[] {
  if (source.gh === undefined) return [{ field: "source.app", op: "==", value: source.app }];
  const out: Constraint[] = [{ field: "ghGroup", op: "==", value: source.gh }];
  if (source.repo !== undefined) out.push({ field: "source.repo", op: "==", value: source.repo });
  else if (source.item !== undefined) out.push({ field: "source.item", op: "==", value: source.item });
  return out;
}

function oneOrIn(field: string, ids: readonly string[]): Constraint {
  return ids.length === 1 ? { field, op: "==", value: ids[0] } : { field, op: "in", value: [...ids] };
}

/** `mail.search`'s scope arguments for the same scope (accounts resolved for several workspaces). */
export function searchScope(
  scope: Scope,
  accounts: ReadonlyArray<{ id: string; workspaceId: string }>,
): { workspaceId?: string; accountIds?: string[] } {
  if (scope.kind === "all") return {};
  if (scope.kind === "accounts") return { accountIds: scope.ids };
  if (scope.ids.length === 1) return { workspaceId: scope.ids[0] as string };
  return { accountIds: accounts.filter((a) => scope.ids.includes(a.workspaceId)).map((a) => a.id) };
}

/** The URL `scope` value for the switcher's current choice (undefined = the path's workspace). */
export function scopeParam(choice: string, pathWorkspace: string): string | undefined {
  return choice === `ws:${pathWorkspace}` ? undefined : choice;
}
